import React, { useEffect, useState } from "react";
import apiService from "../../../../service/Api";
import {
  Grid,
  Box,
  Typography,
  Pagination,
  PaginationItem,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../theme";
import { EnhancedMetricCard } from "../admin-widgets/AdminDashboard";
import {
  EventNote,
  StarRounded,
  TrendingDown,
  TrendingUp,
} from "@mui/icons-material";
import VenueMap from "./VenueMap";

import MostBookedVenuesLeaderboard from "./components/MostBookedVenues";
import { useNavigate } from "react-router-dom";
import VenueWidgets from "./components/widgets/VenueWidgets";
import SpeedByVenueType from "./components/widgets/SpeedByVenueType";
import CapacitySpeedQuality from "./components/widgets/CapacitySpeedQuality";
import CapacitySpeedHeatmap from "./components/widgets/CapacitySpeedHeatMap";
import SurburbSpeedWidget from "./components/widgets/SurburbSpeedWidget";
import CitySuburbWidget from "./components/widgets/CitySurburbWidget";
import ISPPerformanceWidget from "./components/widgets/ISPPerformanceWidget";
import VenueTypesWidget from "./components/widgets/VenueTypesWidget";
import VenueCapacityWidget from "./components/widgets/VenueCapacityWidget";
import StreamingReadinessWidget from "./components/widgets/StreamReadinessWidget";
import TotalCapacityByVenueWidget from "./components/widgets/TotalCapacityByVenue";
import TopSurburbsByCapacity from "./components/widgets/TopSurburbsBycapacity";

import { ALL_WIDGETS } from "../../../configurations/venues-admin-config/VenuesWidgetsAdmin";
import CreatorInsightsWidget from "./components/widgets/CreatorInsights";
import VenueLeaderboard2 from "./components/VenueLeaderBoard2";

const WIDGET_COMPONENTS: Record<string, React.ReactNode> = {
  speed_by_type: <SpeedByVenueType />,
  capacity_speed_quality: <CapacitySpeedQuality />,
  capacity_speed_heatmap: <CapacitySpeedHeatmap />,
  isp_performance: <ISPPerformanceWidget />,
  stream_readiness: <StreamingReadinessWidget />,
  top_surburbs_by_capacity: <TopSurburbsByCapacity />,
  creator_insights: <CreatorInsightsWidget />,
  // top_10_contributors: <TopContributorsWidget />,
  // average_speed: <AverageSpeedWidget />,
  // average_score: <AverageScoreWidget />,
  venue_types_pie: <VenueTypesWidget />,
  city_suburb_hierarchy: <CitySuburbWidget />,
  total_capacity_by_venue: <TotalCapacityByVenueWidget />,
  venue_types_by_capacity: <VenueCapacityWidget />,
  suburb_speed_rank: <SurburbSpeedWidget />,
};

type UserItem = {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  // any other fields you need
};
type SiteVisitAssignee = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
};

type SiteVisit = {
  id: number;
  status: string;
  scheduled_date_time: string;
  completed_date_time: string | null;
  assignees: SiteVisitAssignee[];
};

type VenueItem = {
  id: number;
  name: string | null;
  address_one?: string | null;
  address_two?: string | null;
  suburb?: string | null;
  city?: string | null;
  location?: string | null;
  capacity?: string | null;
  completeness_score?: number | null;
  created_by: UserItem;
  assigned_to?: UserItem | null;
  latest_site_visit?: SiteVisit | null;
};

type VenuesApiResponse = {
  results: VenueItem[];
};

type BackendResponse = {
  total_venues: number;
  summary: {
    above_75: number;
    between_50_74: number;
    between_25_49: number;
    between_1_24: number;
  };
  top10: VenueItem[];
  top10_individuals: VenueItem[];
  lowest10: VenueItem[];
  all_venues?: VenueItem[];
};

const VenuesDashboard: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [data, setData] = useState<BackendResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  const getUserDisplayName = (user?: UserItem | SiteVisitAssignee) => {
    if (!user) return "N/A";

    if (user.first_name || user.last_name) {
      return `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
    }

    if (user.email) {
      const nameFromEmail = user.email.split("@")[0];
      return nameFromEmail;
    }

    return "N/A";
  };
  const [activeWidgets, setActiveWidgets] = useState<string[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem("venueActiveWidgets");
    if (stored) {
      setActiveWidgets(JSON.parse(stored));
    }
  }, []);

  // Listen for storage changes (e.g., admin updated active widgets)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "venueActiveWidgets") {
        const newActive = e.newValue ? JSON.parse(e.newValue) : [];
        setActiveWidgets(newActive);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const [selectedIndividual, setSelectedIndividual] = useState<number | "">("");
  const [individualVenues, setIndividualVenues] = useState<VenueItem[]>([]);
  const [loadingIndividuals, setLoadingIndividuals] = useState(false);

  const [users, setUsers] = useState<UserItem[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [pageTop, setPageTop] = useState(1);
  const [pageIndividuals, setPageIndividuals] = useState(1);
  const [pageLowest, setPageLowest] = useState(1);
  const [pageAllVenues, setPageAllVenues] = useState(1);

  const venuesPerPage = 5;
  const venuesPerPageList = 5;

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await apiService.get<BackendResponse>(
          "/api/management/dashboard/venues-statistics/",
        );
        console.log("all venues ", res.data);

        setData(res.data);
      } catch (err: unknown) {
        setError(
          (err as { message?: string })?.message ??
            "Failed to fetch venues statistics.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);
  //   useEffect(() => {
  //   const fetchAllUsers = async () => {
  //     try {
  //       setLoadingUsers(true);

  //       // 1. Fetch all users
  //       let allUsers: UserItem[] = [];
  //       let url: string | null = "/api/authentication/users/";

  //       while (url) {
  //         const response: {
  //           data: { results: UserItem[]; next: string | null };
  //         } = await apiService.get(url);

  //         allUsers = [...allUsers, ...response.data.results];
  //         url = response.data.next;
  //       }

  //       // 2. Filter users who created at least one venue
  //       const usersWithVenues: UserItem[] = [];

  //      for (const user of allUsers) {
  //        const venueResponse = await apiService.get(
  //          `/api/venues/venue/?created_by=${user.id}`
  //        );

  //        const data = venueResponse.data as {
  //          count: number;
  //          next: string | null;
  //          previous: string | null;
  //          results: any[];
  //        };

  //        if (Array.isArray(data.results) && data.results.length > 0) {
  //          usersWithVenues.push(user);
  //        }
  //      }

  //       setUsers(usersWithVenues);
  //       console.log("Users with venues",usersWithVenues)
  //     } catch (err) {
  //       console.error("Failed to fetch users", err);
  //       setUsers([]);
  //     } finally {
  //       setLoadingUsers(false);
  //     }
  //   };

  //   fetchAllUsers();
  // }, []);

  useEffect(() => {
    const fetchAllUsersWithVenues = async () => {
      try {
        setLoadingUsers(true);

        let allVenues: VenueItem[] = [];
        let url: string | null = "/api/venues/venue/";

        while (url) {
          // Explicitly type response
          const response: { data: VenuesApiResponse & { next?: string } } =
            await apiService.get(url);

          allVenues = [...allVenues, ...response.data.results];

          // Use next from response
          url = response.data.next ?? null;
        }

        const uniqueUsersMap = new Map<number, UserItem>();
        allVenues.forEach((venue) => {
          if (venue.created_by && !uniqueUsersMap.has(venue.created_by.id)) {
            uniqueUsersMap.set(venue.created_by.id, venue.created_by);
          }
        });

        setUsers(Array.from(uniqueUsersMap.values()));
        console.log(
          "Users with at least one venue:",
          Array.from(uniqueUsersMap.values()),
        );
      } catch (err) {
        console.error("Failed to fetch users", err);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchAllUsersWithVenues();
  }, []);

  useEffect(() => {
    if (!selectedIndividual) return;

    const fetchIndividualVenues = async () => {
      try {
        setLoadingIndividuals(true);
        setIndividualVenues([]); // clear old data while loading

        const res = await apiService.get<VenuesApiResponse>(
          `/api/venues/venue/?created_by=${selectedIndividual}`,
        );

        const venues = res.data?.results || [];

        // Sort by completeness_score descending and take top 10
        const top10 = venues
          .sort(
            (a, b) => (b.completeness_score ?? 0) - (a.completeness_score ?? 0),
          )
          .slice(0, 10);

        setIndividualVenues(top10);
        setPageIndividuals(1); // reset pagination
      } catch (err) {
        console.error("Failed to fetch individual venues", err);
        setIndividualVenues([]);
      } finally {
        setLoadingIndividuals(false);
      }
    };

    fetchIndividualVenues();
  }, [selectedIndividual]);

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );

  if (!data)
    return (
      <Box sx={{ p: 3 }}>
        <Typography>No data available.</Typography>
      </Box>
    );

  // Data
  const topVenues = data.top10 || [];
  const topIndividuals = selectedIndividual ? individualVenues : [];

  const lowestVenues = data.lowest10 || [];
  const allVenues = data.all_venues || [];

  const parseLocationCoords = (location?: string | null) => {
    if (!location) return { lat: null, lng: null };
    const [latStr, lngStr] = location.split(",").map((part) => part.trim());
    const lat = Number(latStr);
    const lng = Number(lngStr);
    return {
      lat: Number.isFinite(lat) ? lat : null,
      lng: Number.isFinite(lng) ? lng : null,
    };
  };

  const mapVenues = allVenues.map((venue) => {
    const { lat, lng } = parseLocationCoords(venue.location);
    const address = [
      venue.address_one,
      venue.address_two,
      venue.suburb,
      venue.city,
    ]
      .filter(Boolean)
      .join(", ");

    return {
      id: venue.id,
      name: venue.name ?? "Unknown Venue",
      address,
      lat,
      lng,
      status: "active" as const,
    };
  });

  // Pagination
  const totalPagesTop = Math.max(
    1,
    Math.ceil(topVenues.length / venuesPerPage),
  );
  const totalPagesIndividuals = Math.max(
    1,
    Math.ceil(topIndividuals.length / venuesPerPage),
  );
  const totalPagesLowest = Math.max(
    1,
    Math.ceil(lowestVenues.length / venuesPerPage),
  );
  const totalPagesAllVenues = Math.max(
    1,
    Math.ceil(allVenues.length / venuesPerPageList),
  );

  const paginatedTop = topVenues.slice(
    (pageTop - 1) * venuesPerPage,
    pageTop * venuesPerPage,
  );
  const paginatedIndividuals = topIndividuals.slice(
    (pageIndividuals - 1) * venuesPerPage,
    pageIndividuals * venuesPerPage,
  );
  const paginatedLowest = lowestVenues.slice(
    (pageLowest - 1) * venuesPerPage,
    pageLowest * venuesPerPage,
  );
  const paginatedAllVenues = allVenues.slice(
    (pageAllVenues - 1) * venuesPerPageList,
    pageAllVenues * venuesPerPageList,
  );

  const {
    above_75: countAbove75,
    between_50_74: count50to74,
    between_25_49: count25to49,
    between_1_24: count1to24,
  } = data.summary;

  const renderVenueRow = (
    v: VenueItem,
    index: number,
    page: number,
    hideCreatedAssigned: boolean = false,
  ) => {
    const displayIndex = (page - 1) * venuesPerPage + index + 1;
    const createdByName = getUserDisplayName(v.created_by);
    const assignedToName = v.latest_site_visit?.assignees?.length
      ? getUserDisplayName(v.latest_site_visit.assignees[0])
      : "N/A";

    return (
      <Box
        key={v.id}
        sx={{
          display: "flex",
          flexDirection: "column",
          px: 1,
          py: 0.6,
          borderBottom: "1px solid #f0f0f0",
          cursor: "pointer",
          "&:hover": { backgroundColor: "background.paper" },
        }}
        onClick={() => navigate(`/venues/view/${v.id}`)}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: "bold" }}>{displayIndex}.</Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: "text.primary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "normal",
              }}
              title={v.name ?? "Unnamed venue"}
            >
              {v.name ?? "Unnamed venue"}
            </Typography>
          </Box>
          <Typography sx={{ fontWeight: 500 }}>
            {v.completeness_score ?? "-"}%
          </Typography>
        </Box>

        <Typography
          sx={{
            color: "text.secondary",
            ml: 3.5,
            mt: 0.2,
            fontSize: "0.85rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={v.address_one ?? v.suburb ?? v.city ?? "No address provided"}
        >
          {v.address_one ?? v.suburb ?? v.city ?? "No address provided"}
        </Typography>

        {/* Only show Created By & Assigned To if flag is false */}
        {!hideCreatedAssigned && (
          <Box
            sx={{
              ml: 3.5,
              mt: 1,
              display: "flex",
              flexDirection: "column",
              gap: 0.3,
              opacity: 0.8,
            }}
          >
            <Typography
              sx={{ fontSize: "0.7rem", color: "text.primary" }}
              title={`Created by: ${createdByName}`}
            >
              Created by: {createdByName}
            </Typography>
            <Typography
              sx={{ fontSize: "0.7rem", color: "text.primary" }}
              title={`Assigned to: ${assignedToName}`}
            >
              Assigned to: {assignedToName}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ flex: 1 }}>
      <Grid container spacing={2} wrap="nowrap">
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <EnhancedMetricCard
            title="Total venues"
            value={data.total_venues}
            icon={EventNote}
            color="#f59e0b"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <EnhancedMetricCard
            title="Venues 75>"
            value={countAbove75}
            icon={StarRounded}
            color="#10b981"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <EnhancedMetricCard
            title="Venues 50>74"
            value={count50to74}
            icon={TrendingUp}
            color="#4285f4"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <EnhancedMetricCard
            title="Venues 25>49"
            value={count25to49}
            icon={TrendingDown}
            color="#f59e0b"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <EnhancedMetricCard
            title="Venues 1>24"
            value={count1to24}
            icon={TrendingDown}
            color="#614338"
          />
        </Grid>
      </Grid>

      {/* Top, Individual, Lowest */}
      <Grid
        container
        spacing={2}
        sx={{ mt: 2, justifyContent: "space-between", alignItems: "stretch" }}
      >
        {/* Top 10 Venues */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: colors.primary[900],
              flex: 1,
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              justifyContent: "space-between",
              height: "100%", // <-- add this
            }}
          >
            <Typography
              variant="h6"
              textAlign="center"
              sx={{ mb: 1, fontWeight: "bold" }}
            >
              Top 10 Venues
            </Typography>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.8,
              }}
            >
              {paginatedTop.length === 0 ? (
                <Typography sx={{ color: "text.secondary", textAlign: "center", mt: 2 }}>
                  No top venues available.
                </Typography>
              ) : (
                paginatedTop.map((v, i) => renderVenueRow(v, i, pageTop))
              )}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Pagination
                count={totalPagesTop}
                page={pageTop}
                onChange={(_, val) => setPageTop(val)}
                color="primary"
                size="small"
                renderItem={(item) =>
                  item.type === "previous" || item.type === "next" ? (
                    <PaginationItem {...item} />
                  ) : null
                }
              />
            </Box>
          </Box>
        </Grid>

        {/* Top 10 by Individuals */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: colors.primary[900],
              flex: 1,
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              justifyContent: "space-between",
              height: "100%", // <-- add this
            }}
          >
            <Box>
              <Typography
                variant="h6"
                textAlign="center"
                sx={{ mb: 1, fontWeight: "bold" }}
              >
                Top 10 Venues by Individuals
              </Typography>

              <FormControl size="small" sx={{ mb: 1, width: "100%" }}>
                <Select
                  displayEmpty
                  value={selectedIndividual}
                  onChange={(e) =>
                    setSelectedIndividual(e.target.value as number)
                  }
                  MenuProps={{
                    PaperProps: { style: { maxHeight: 250, width: 250 } },
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                  }}
                >
                  <MenuItem value="">Select Individual</MenuItem>
                  {loadingUsers ? (
                    <MenuItem disabled>Loading users...</MenuItem>
                  ) : (
                    users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>

            {/* Venue list */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.8,
              }}
            >
              {loadingIndividuals ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : paginatedIndividuals.length === 0 ? (
                <Typography sx={{ color: "text.secondary", textAlign: "center", mt: 2 }}>
                  No individual-ranked venues available.
                </Typography>
              ) : (
                paginatedIndividuals.map(
                  (v, i) => renderVenueRow(v, i, pageIndividuals, true), // hide created/assigned
                )
              )}
            </Box>

            {/* Pagination fixed at bottom */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Pagination
                count={totalPagesIndividuals}
                page={pageIndividuals}
                onChange={(_, val) => setPageIndividuals(val)}
                color="primary"
                size="small"
                renderItem={(item) =>
                  item.type === "previous" || item.type === "next" ? (
                    <PaginationItem {...item} />
                  ) : null
                }
              />
            </Box>
          </Box>
        </Grid>

        {/* Lowest 10 Venues */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: colors.primary[900],
              flex: 1,
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              justifyContent: "space-between",
              height: "100%", // <-- add this
            }}
          >
            <Typography
              variant="h6"
              textAlign="center"
              sx={{ mb: 1, fontWeight: "bold" }}
            >
              Lowest 10 Venues
            </Typography>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.8,
              }}
            >
              {paginatedLowest.length === 0 ? (
                <Typography sx={{ color: "text.secondary", textAlign: "center", mt: 2 }}>
                  No lowest venues available.
                </Typography>
              ) : (
                paginatedLowest.map((v, i) => renderVenueRow(v, i, pageLowest))
              )}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Pagination
                count={totalPagesLowest}
                page={pageLowest}
                onChange={(_, val) => setPageLowest(val)}
                color="primary"
                size="small"
                renderItem={(item) =>
                  item.type === "previous" || item.type === "next" ? (
                    <PaginationItem {...item} />
                  ) : null
                }
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* All Venues */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: colors.primary[900],
              height: 320,
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              textAlign="center"
              sx={{ fontWeight: "bold", mb: 1 }}
            >
              All Venues
            </Typography>

            <Box sx={{ flex: 1, mt: 1 }}>
              <Box sx={{ flex: 1, mt: 1 }}>
                {paginatedAllVenues.length === 0 ? (
                  <Typography
                    sx={{ color: "text.secondary", textAlign: "center", mt: 2 }}
                  >
                    No venues available.
                  </Typography>
                ) : (
                  paginatedAllVenues.map((v, i) => {
                    const displayIndex =
                      (pageAllVenues - 1) * venuesPerPageList + i + 1;

                    return (
                      <Box
                        key={v.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: 1,
                          py: 0.4,
                          borderBottom: "1px solid #f0f0f0",
                          height: 40,
                          wordBreak: "break-word",
                          cursor: "pointer",

                          "&:hover": { backgroundColor: "background.paper" },
                        }}
                        onClick={() => navigate(`/venues/view/${v.id}`)}
                      >
                        <Box sx={{ width: "75%" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography sx={{ fontWeight: "bold" }}>
                              {displayIndex}.
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={v.name ?? "Unnamed venue"}
                            >
                              {v.name ?? "Unnamed venue"}
                            </Typography>
                          </Box>

                          <Typography
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.75rem", // smaller font
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              ml: 3.5,
                            }}
                            title={
                              v.address_one ??
                              v.suburb ??
                              v.city ??
                              "No address provided"
                            }
                          >
                            {v.address_one ??
                              v.suburb ??
                              v.city ??
                              "No address provided"}
                          </Typography>
                        </Box>

                        <Typography
                          sx={{ fontWeight: 500, fontSize: "0.75rem" }}
                        >
                          {v.completeness_score ?? "-"}%
                        </Typography>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Pagination
                count={totalPagesAllVenues}
                page={pageAllVenues}
                onChange={(_, val) => setPageAllVenues(val)}
                color="primary"
                size="small"
                renderItem={(item) =>
                  item.type === "previous" || item.type === "next" ? (
                    <PaginationItem {...item} />
                  ) : null
                }
              />
            </Box>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: colors.primary[900],
              height: 320,
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <VenueMap data={{ venues: mapVenues }} />
            </Box>
          </Box>
        </Grid>

        <Grid
          container
          spacing={2}
          sx={{ mt: 2, width: "100%", alignItems: "stretch" }}
        >
          {/* Most Booked Venues Leaderboard */}
          <Grid size={{ xs: 12, md: 6 }}>
            <MostBookedVenuesLeaderboard />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <VenueLeaderboard2 />
          </Grid>
          {activeWidgets.length > 0 && (
            <Grid container spacing={2} sx={{ mt: 2, width: "100%" }}>
              {activeWidgets.map((id) => {
                const Component = WIDGET_COMPONENTS[id];
                const widgetInfo = ALL_WIDGETS.find((w) => w.id === id);
                if (!Component || !widgetInfo) return null;

                const isSuburbSpeed = id === "suburb_speed_rank";

                return (
                  <Grid key={id} size={{ xs: 12, md: isSuburbSpeed ? 12 : 6 }}>
                    <VenueWidgets
                      title={widgetInfo.title}
                      sx={{ width: "100%", height: "100%" }}
                    >
                      {Component}
                    </VenueWidgets>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default VenuesDashboard;
